import { message } from 'antd';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { DURATION_TIMER } from 'appConstants';
import { makeSelectAuthUser } from 'containers/App/selectors';

import {
  createCreditCardFail,
  createCreditCardSuccess,
  creditCardsGet,
  creditCardsGetFail,
  creditCardsGetSuccess,
  deleteCreditCardFail,
  deleteCreditCardSuccess,
  getCreditCard,
  getCreditCardFail,
  getCreditCardSuccess,
  updateCreditCardFail,
  updateCreditCardSuccess
} from './actions';
import {
  createCreditCard,
  deleteCreditCardApi,
  getCreditCardApi,
  getCreditCards,
  updateCreditCard
} from './api';
import {
  CREATE_CREDIT_CARD,
  DELETE_CREDIT_CARD,
  GET_CREDIT_CARD,
  GET_CREDIT_CARDS,
  UPDATE_CREDIT_CARD
} from './constants';
import { makeSelectCreditCardsParams } from './selectors';

export function* getCreditCardList(action) {
  try {
    const authUser = yield select(makeSelectAuthUser());
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authUser.bearer_token}`
    };
    const response = yield call(getCreditCards, action.payload, headers);
    if (response.error) {
      yield put(creditCardsGetFail(response.error));
      message.error(response.error.message, DURATION_TIMER);
    } else {
      yield put(creditCardsGetSuccess(response));
    }
  } catch (error) {
    yield put(creditCardsGetFail(error));
    message.error(error.message, DURATION_TIMER);
  }
}

export function* creditCardCreateSaga(action) {
  const { payload, onSuccess, onError } = action;
  try {
    const authUser = yield select(makeSelectAuthUser());
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authUser.bearer_token}`
    };
    const response = yield call(createCreditCard, payload, headers);
    if (response.status !== 201) {
      yield put(createCreditCardFail(response));
      message.error(response.data.message, DURATION_TIMER);
      if (onError) onError(response);
    } else {
      yield put(createCreditCardSuccess(response));
      message.success('Credit card created successfully', DURATION_TIMER);
      const params = yield select(makeSelectCreditCardsParams());
      yield put(creditCardsGet(params));
      if (onSuccess) onSuccess(response);
    }
  } catch (error) {
    yield put(createCreditCardFail(error));
    message.error(error.message, DURATION_TIMER);
    if (onError) onError(error);
  }
}

export function* getCreditCardById(action) {
  const authUser = yield select(makeSelectAuthUser());

  let headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authUser.bearer_token}`
  };

  try {
    const response = yield call(getCreditCardApi, action.payload, headers);
    yield put(getCreditCardSuccess(response));
  } catch (e) {
    if (e.data.message) {
      message.error(e.data.message);
    } else {
      message.error('Failed to load Credit Card');
    }
    yield put(getCreditCardFail(e.data));
  }
}

export function* updateCreditCardSaga(action) {
  const { payload, onSuccess, onError } = action;
  const authUser = yield select(makeSelectAuthUser());

  let headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authUser.bearer_token}`
  };

  try {
    const { id, ...body } = payload;
    const response = yield call(updateCreditCard, body, { id }, headers);
    if (response.status !== 200) {
      yield put(updateCreditCardFail(response.data));
      message.error(response.data.message, DURATION_TIMER);
      if (onError) onError(response.data);
    } else {
      yield put(updateCreditCardSuccess(response.data));
      message.success('Credit card updated successfully', DURATION_TIMER);
      const params = yield select(makeSelectCreditCardsParams());
      yield put(creditCardsGet(params));
      yield put(getCreditCard(id));
      if (onSuccess) onSuccess(response.data);
    }
  } catch (e) {
    yield put(updateCreditCardFail(e));
    message.error(e.message, DURATION_TIMER);
    if (onError) onError(e);
  }
}

export function* deleteCreditCardSaga(action) {
  const authUser = yield select(makeSelectAuthUser());

  let headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authUser.bearer_token}`
  };

  try {
    yield call(deleteCreditCardApi, action.payload, headers);
    yield put(deleteCreditCardSuccess(action.payload));
    message.success('Credit card deleted successfully', DURATION_TIMER);
  } catch (e) {
    yield put(deleteCreditCardFail(e.response || e));
    message.error(e.response?.data?.message || 'Failed to delete Credit Card', DURATION_TIMER);
  }
}

function* creditCardSaga() {
  yield takeLatest(CREATE_CREDIT_CARD, creditCardCreateSaga);
  yield takeLatest(GET_CREDIT_CARDS, getCreditCardList);
  yield takeLatest(GET_CREDIT_CARD, getCreditCardById);
  yield takeLatest(UPDATE_CREDIT_CARD, updateCreditCardSaga);
  yield takeLatest(DELETE_CREDIT_CARD, deleteCreditCardSaga);
}

export default creditCardSaga;
